import store from '../../../../store/store';
import { COUNTRY_FACET, CreateCountry, FilterAction } from '../interfaces/create-search-filter.interface';
import { SearchFacet, SearchFacetValue } from '../../../search/interfaces/search.interface';
import { StoreState } from '../../../../store/store.interface';
import { createSearchParam } from '../store/create-search.selector';
import { getCreateSearchRequest, performCreateSearch } from '../thunks/create-search.thunks';
import { setCreateSearchLoading } from '../store/create-search.store';

/**
 * Announces the filter change to screen readers.
 * The message is based on the action taken and the facet and value involved in the change.
 *
 * @param action
 * @param facet
 * @param value
 * @param announcer
 */
export function announceFilterChange(
  action: FilterAction,
  facet?: SearchFacet,
  value?: SearchFacetValue,
  announcer?: HTMLElement | null
): void {
  if (announcer) {
    const updateResultsText = 'Search results have been updated.';

    switch (action) {
      case FilterAction.ADD:
        announcer.textContent = `${value?.label} ${facet?.label} filter added. ${updateResultsText}`;
        break;
      case FilterAction.CHANGE:
        announcer.textContent = `${facet?.label} changed to ${value?.label}. ${updateResultsText}`;
        break;
      case FilterAction.CLEAR_ALL:
        announcer.textContent = `All filters cleared. ${updateResultsText}`;
        break;
      case FilterAction.CLEAR:
        announcer.textContent = `${value?.label} ${facet?.label} filter removed. Other filters have been cleared. ${updateResultsText}`;
        break;
      case FilterAction.CLEAR_AND_CHANGE:
        announcer.textContent = `${facet?.label} changed to ${value?.label}. Other filters have been cleared. ${updateResultsText}`;
        break;
      case FilterAction.REMOVE:
        announcer.textContent = `${value?.label} ${facet?.label} filter removed. ${updateResultsText}`;
        break;
    }
  }
}

/**
 * Converts the static list of countries into a SearchFacet object that can be used in the country filter.
 *
 * @param countries
 */
export function getCountriesAsFacet(countries?: CreateCountry[]): SearchFacet {
  const facetValues = countries?.map((country) => {
    return createFacetValue(country.code, country.description);
  });

  return createFacet(COUNTRY_FACET, 'Country', facetValues || []);
}

/**
 * Converts the facet values into an object for dropdown options.
 *
 * @param facetValue
 */
export function getFacetValuesAsObject(facetValue?: SearchFacetValue[]): { [key: string]: string } | undefined {
  return facetValue?.reduce((result, value) => {
    result[value.name] = value.label;
    return result;
  }, {});
}

/**
 * Updates the search filters based on the facet, value, and action provided.
 * This offers more flexibility than for regular search.
 *
 * @param facet
 * @param value
 * @param action
 * @param storeCategory
 * @param shouldPerformCreateSearch
 */
export async function updateFacet(
  facet: string,
  value: SearchFacetValue,
  action: FilterAction,
  storeCategory?: string,
  shouldPerformCreateSearch = true
): Promise<void> {
  const state = store.getState() as StoreState;
  const searchString: string = createSearchParam(state)!;
  const baseParams = {
    excludeFacetName: facet,
    searchValue: searchString,
    start: 1,
    store: storeCategory
  };
  let params = {};

  switch (action) {
    case FilterAction.ADD:
      params = {
        ...baseParams,
        addFacetName: facet,
        addFacetValue: value.name,
      };
      break;
    case FilterAction.CLEAR:
      params = {
        ...baseParams,
        clearFilter: true,
      };
      break;
    case FilterAction.CHANGE:
    case FilterAction.CLEAR_AND_CHANGE:
      params = {
        ...baseParams,
        ...(action === FilterAction.CLEAR_AND_CHANGE ? { clearFilter: true } : {}),
        changeFacetName: facet,
        changeFacetValue: value.name,
      };
      break;
    case FilterAction.REMOVE:
      params = {
        ...baseParams,
        removeFacetName: facet,
        removeFacetValue: value.name,
      };
      break;
    default:
      params = baseParams;
      break;
  }

  if (shouldPerformCreateSearch) {
    await store.dispatch(performCreateSearch(params));
  } else {
    await store.dispatch(setCreateSearchLoading(getCreateSearchRequest(params, state)));
  }
}

/**
 * Creates a default SearchFacet object with the provided name, label, and facet values.
 *
 * @param name
 * @param label
 * @param facetValues
 */
export function createFacet(name: string, label: string, facetValues: SearchFacetValue[]): SearchFacet {
  return {
    name: name,
    label: label,
    facetValues: facetValues,
  };
}

/**
 * Creates a default SearchFacetValue object with the provided name, label, and selected status.
 *
 * @param name
 * @param label
 * @param selected
 */
export function createFacetValue(name: string, label: string, selected = false): SearchFacetValue {
  return {
    name: name,
    label: label,
    count: 0,
    selected: selected,
  };
}
